import CryptoJS from 'crypto-js'
import { isString } from 'lodash'

// 默认密钥和初始化向量
const DEFAULT_KEY = 'AJ03lQmVmtomCfug'
const DEFAULT_IV = '1e5673b2572af26a8364a50af84c7d2aS'

const DEFAULT_KEY_IV = { key: DEFAULT_KEY, iv: DEFAULT_IV }

/**
 * 生成AES密钥 - 使用SHA-256哈希
 * @param {string} key - 密钥字符串
 * @returns {CryptoJS.lib.WordArray} - 32字节的密钥
 */
function makeKey(key = DEFAULT_KEY) {
  // 使用SHA-256对key进行哈希，生成32字节（256位）密钥
  return CryptoJS.SHA256(key);
}

/**
 * 生成初始化向量
 * @param {string} iv - 十六进制字符串（32字符=16字节）
 * @returns {CryptoJS.lib.WordArray} - IV的WordArray表示
 */
function makeIv(iv = DEFAULT_IV) {
  // 将十六进制字符串解析为字节数组
  return CryptoJS.enc.Hex.parse(iv);
}

/**
 * 核心加密方法 - AES/CBC/PKCS7加密
 * @param {string} plainText - 要加密的明文
 * @param {string} [key] - 密钥字符串（将使用SHA-256哈希处理），默认使用 DEFAULT_KEY
 * @param {string} [iv] - 初始化向量（十六进制字符串，32字符=16字节），默认使用 DEFAULT_IV
 * @returns {string} - 加密后的十六进制字符串
 */
export function encrypt(plainText, key = DEFAULT_KEY, iv = DEFAULT_IV) {
  try {
    // 1. 使用SHA-256对key进行哈希，生成32字节密钥
    const keyBytes = makeKey(key);

    // 2. 解析IV（从十六进制字符串转换）
    const ivBytes = makeIv(iv);

    // 3. 将明文转换为UTF-8编码的WordArray
    const plainTextBytes = CryptoJS.enc.Utf8.parse(plainText);

    // 4. 执行AES/CBC/PKCS7加密
    const encrypted = CryptoJS.AES.encrypt(plainTextBytes, keyBytes, {
      iv: ivBytes,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // 5. 将加密结果转换为十六进制字符串（小写）
    return encrypted.ciphertext.toString(CryptoJS.enc.Hex);

  } catch (e) {
    console.error('encrypt error:', e);
    return '';
  }
}

/**
 * 核心解密方法 - AES/CBC/PKCS7解密
 * @param {string} cipherText - 加密的十六进制字符串
 * @param {string} [key] - 密钥字符串（将使用SHA-256哈希处理），默认使用 DEFAULT_KEY
 * @param {string} [iv] - 初始化向量（十六进制字符串，32字符=16字节），默认使用 DEFAULT_IV
 * @returns {string} - 解密后的明文
 */
export function decrypt(cipherText, key = DEFAULT_KEY, iv = DEFAULT_IV) {
  try {
    // 1. 使用SHA-256对key进行哈希，生成32字节密钥
    const keyBytes = makeKey(key);

    // 2. 解析IV（从十六进制字符串转换）
    const ivBytes = makeIv(iv);

    // 3. 将十六进制密文转换为CipherParams对象
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Hex.parse(cipherText)
    });

    // 4. 执行AES/CBC/PKCS7解密
    const decrypted = CryptoJS.AES.decrypt(cipherParams, keyBytes, {
      iv: ivBytes,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // 5. 将解密结果转换为UTF-8字符串
    return decrypted.toString(CryptoJS.enc.Utf8);

  } catch (e) {
    console.error('decrypt error:', e);
    return null;
  }
}

/**
 * 使用KeyIv对象加密字符串
 * @param {string} plainText - 要加密的明文
 * @param {Object} [keyIv] - 包含key和iv的对象 { key: string, iv: string }，如果不传则使用默认值
 * @returns {string} - 加密后的十六进制字符串
 */
export function encryptWithKeyIv(plainText, keyIv = DEFAULT_KEY_IV) {
  if (keyIv && keyIv.key && keyIv.iv) {
    return encrypt(plainText, keyIv.key, keyIv.iv);
  }
  return encrypt(plainText);
}

/**
 * 使用KeyIv对象解密字符串
 * @param {string} cipherText - 加密的十六进制字符串
 * @param {Object} [keyIv] - 包含key和iv的对象 { key: string, iv: string }，如果不传则使用默认值
 * @returns {string} - 解密后的明文
 */
export function decryptWithKeyIv(cipherText, keyIv = DEFAULT_KEY_IV) {
  if (keyIv && keyIv.key && keyIv.iv) {
    return decrypt(cipherText, keyIv.key, keyIv.iv);
  }
  return decrypt(cipherText);
}

/**
 * 加密整数值
 * @param {number} value - 要加密的整数
 * @param {Object} [keyIv] - 包含key和iv的对象 { key: string, iv: string }，如果不传则使用默认值
 * @returns {string} - 加密后的十六进制字符串
 */
export function encryptInt(value, keyIv = DEFAULT_KEY_IV) {
  // 将int转换为无符号数（与Java的 value & 0xFFFFFFFFL 等效）
  const unsignedValue = value >>> 0;
  if (keyIv && keyIv.key && keyIv.iv) {
    return encrypt(String(unsignedValue), keyIv.key, keyIv.iv);
  }
  return encrypt(String(unsignedValue));
}

/**
 * 解密为整数
 * @param {string} cipherText - 加密的十六进制字符串
 * @param {Object} [keyIv] - 包含key和iv的对象 { key: string, iv: string }，如果不传则使用默认值
 * @returns {number} - 解密后的整数，解密失败返回0
 */
export function decryptInt(cipherText, keyIv = { key: DEFAULT_KEY, iv: DEFAULT_IV }) {
  try {
    const decrypted = decryptWithKeyIv(cipherText, keyIv);
    return parseInt(decrypted, 10);
  } catch (e) {
    console.error('decryptInt error:', e);
    return 0;
  }
}

/**
 * IM消息内容解密
 * 如果内容是_agju结尾的，则去除掉后缀后使用AES进行解密
 * @param {string} content - 加密的消息内容
 * @returns {string} - 解密后的明文，如果不需要解密则返回原内容
 */
export function IMContentDecrypt(content) {
  if (!content || !isString(content)) {
    return content;
  }

  // 检查是否有后缀
  if (content.endsWith('_agju')) {
    const cipherStr = content.substring(0, content.indexOf('_agju'));
    const decrypted = decryptWithKeyIv(cipherStr);
    return decrypted || content; // 解密失败时返回原内容
  }

  return content;
}

/**
 * IM消息内容加密
 * 加密完成后添加_agju后缀
 * @param {string} content - 要加密的明文
 * @returns {string} - 加密后的内容（带_agju后缀）
 */
export function IMContentEncrypt(content) {
  if (!content || !isString(content)) {
    return content;
  }

  // 加密内容
  const encrypted = encryptWithKeyIv(content);

  // 添加_agju后缀
  return encrypted + '_agju';
}

if (import.meta.env.MODE !== 'production') {
  window.cryptoUtils = {
    encrypt,
    decrypt,
    encryptWithKeyIv,
    decryptWithKeyIv,
    encryptInt,
    decryptInt,
    IMContentDecrypt,
    IMContentEncrypt,
  }
}
